import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios with global timeout
axios.defaults.timeout = 10000; // 10 seconds for public endpoints

// Create separate axios instance for admin API calls (needs longer timeout)
const adminAxios = axios.create({
  timeout: 30000, // 30 seconds for admin endpoints
  baseURL: API
});

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Generic request function for custom API calls
export const request = async (endpoint, options = {}) => {
  const { method = 'GET', body, headers = {}, timeout = 60000 } = options;
  
  const config = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
      ...headers
    },
    timeout
  };
  
  if (body) {
    config.body = body;
  }
  
  const response = await fetch(`${BACKEND_URL}${endpoint}`, config);
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Request failed: ${response.status}`);
  }
  
  return response.json();
};

// Retry helper for failed requests

// Register new user
export const register = async ({ name, email, password, country }) => {
  const response = await axios.post(`${API}/auth/register`, { name, email, password, country });
  return response.data;
};

const retryRequest = async (requestFn, retries = 2, delay = 1000) => {
  for (let i = 0; i < retries; i++) {
    try {
      return await requestFn();
    } catch (error) {
      if (i === retries - 1) throw error;
      console.warn(`Request failed, retrying (${i + 1}/${retries})...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

// Dossiers
export const getDossiers = async (filters = {}) => {
  const params = new URLSearchParams(filters).toString();
  const response = await axios.get(`${API}/dossiers?${params}`, {
    timeout: 5000  // 5 seconds - should be fast with cache
  });
  return response.data;
};

export const getPopularDossiers = async () => {
  const response = await axios.get(`${API}/dossiers?sort_by=popular&status=active`);
  return response.data;
};

export const getLatestDossiers = async () => {
  const response = await axios.get(`${API}/dossiers?sort_by=latest&status=active`);
  return response.data;
};

export const getDraftDossiers = async () => {
  const response = await axios.get(`${API}/dossiers/drafts/list`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Get published articles for editing
export const getPublishedDossiers = async () => {
  const response = await axios.get(`${API}/dossiers/published/list`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Get featured article (algorithm or manual)
export const getFeaturedDossier = async () => {
  const response = await axios.get(`${API}/dossiers/featured`);
  return response.data;
};

// Set article as featured/main
export const setDossierFeatured = async (dossierId) => {
  const response = await axios.post(`${API}/dossiers/${dossierId}/set-featured`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Remove featured status
export const unsetDossierFeatured = async (dossierId) => {
  const response = await axios.post(`${API}/dossiers/${dossierId}/unset-featured`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Set as breaking news
export const setDossierBreaking = async (dossierId) => {
  const response = await axios.post(`${API}/dossiers/${dossierId}/set-breaking`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Remove breaking news status
export const unsetDossierBreaking = async (dossierId) => {
  const response = await axios.post(`${API}/dossiers/${dossierId}/unset-breaking`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getDossier = async (dossierId) => {
  const response = await axios.get(`${API}/dossiers/${dossierId}`, {
    timeout: 5000  // 5 seconds - should be fast with cache
  });
  return response.data;
};

// Alias for compatibility
export const getDossierById = getDossier;

// Track view for popularity
export const trackDossierView = async (dossierId) => {
  try {
    await axios.post(`${API}/dossiers/${dossierId}/view`);
  } catch (e) { /* ignore */ }
};

// Publish draft
export const publishDossier = async (dossierId) => {
  const response = await axios.post(`${API}/dossiers/${dossierId}/publish`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Regenerate image with optional custom prompt
export const regenerateDossierImage = async (dossierId, customPrompt = null) => {
  const body = customPrompt ? { custom_prompt: customPrompt } : {};
  const response = await axios.post(`${API}/dossiers/${dossierId}/regenerate-image`, body, {
    headers: getAuthHeader(),
    timeout: 120000
  });
  return response.data;
};

// Regenerate image with optional reference images (multipart form)
export const regenerateDossierImageWithRefs = async (dossierId, customPrompt = null, referenceImages = []) => {
  const formData = new FormData();
  
  if (customPrompt) {
    formData.append('custom_prompt', customPrompt);
  }
  
  referenceImages.forEach((file, index) => {
    formData.append('reference_images', file);
  });
  
  const headers = getAuthHeader();
  // Don't set Content-Type for FormData, let browser set it with boundary
  delete headers['Content-Type'];
  
  const response = await axios.post(`${API}/dossiers/${dossierId}/regenerate-image-with-refs`, formData, {
    headers,
    timeout: 120000
  });
  return response.data;
};

// Get AI suggestion for image prompt
export const suggestImagePrompt = async (dossierId) => {
  const response = await axios.get(`${API}/dossiers/${dossierId}/suggest-image-prompt`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Edit with AI observation - NON-BLOCKING with polling
export const editDossierWithAI = async (dossierId, observation, onProgress = null) => {
  // Start the task
  const response = await axios.post(`${API}/dossiers/${dossierId}/edit-with-ai`, 
    { observation },
    { headers: getAuthHeader(), timeout: 10000 }
  );
  
  const { task_id } = response.data;
  
  // If no task_id, it was a synchronous response (backwards compatibility)
  if (!task_id) {
    return response.data;
  }
  
  // Poll for completion
  let attempts = 0;
  const maxAttempts = 60; // 2 minutes max
  
  while (attempts < maxAttempts) {
    await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds
    
    try {
      const statusResponse = await axios.get(`${API}/ai-tasks/${task_id}`, {
        headers: getAuthHeader()
      });
      
      const task = statusResponse.data;
      
      if (onProgress) {
        onProgress(task.progress, task.status);
      }
      
      if (task.status === 'completed') {
        return task.result;
      }
      
      if (task.status === 'failed') {
        throw new Error(task.error || 'La edición falló');
      }
      
      attempts++;
    } catch (pollError) {
      console.error('Error polling task:', pollError);
      attempts++;
    }
  }
  
  throw new Error('Timeout: La edición tardó demasiado');
};

// Get AI suggestions for article improvement - NON-BLOCKING with polling
export const getArticleSuggestions = async (dossierId, onProgress = null) => {
  // Start the suggestions task
  const response = await axios.post(`${API}/dossiers/${dossierId}/suggestions`, 
    {},
    { headers: getAuthHeader(), timeout: 10000 }
  );
  
  const { task_id } = response.data;
  
  // If no task_id, it was a synchronous response (backwards compatibility)
  if (!task_id) {
    return response.data;
  }
  
  // Poll for completion
  let attempts = 0;
  const maxAttempts = 60; // 2 minutes max
  
  while (attempts < maxAttempts) {
    await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds
    
    try {
      const statusResponse = await axios.get(`${API}/ai-tasks/${task_id}`, {
        headers: getAuthHeader()
      });
      
      const task = statusResponse.data;
      
      if (onProgress) {
        onProgress(task.progress, task.status);
      }
      
      if (task.status === 'completed') {
        return task.result;
      }
      
      if (task.status === 'failed') {
        throw new Error(task.error || 'Error al generar sugerencias');
      }
      
      attempts++;
    } catch (pollError) {
      console.error('Error polling suggestions task:', pollError);
      attempts++;
    }
  }
  
  throw new Error('Timeout: Las sugerencias tardaron demasiado');
};

// Regenerate article completely with optional instructions
export const regenerateArticle = async (dossierId, instructions = '', onProgress = null) => {
  // Start the regeneration task
  const response = await axios.post(`${API}/dossiers/${dossierId}/regenerate`, 
    { instructions },
    { headers: getAuthHeader(), timeout: 10000 }
  );
  
  const { task_id } = response.data;
  
  if (!task_id) {
    return response.data;
  }
  
  // Poll for completion
  let attempts = 0;
  const maxAttempts = 90; // 3 minutes max
  
  while (attempts < maxAttempts) {
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    try {
      const statusResponse = await axios.get(`${API}/ai-tasks/${task_id}`, {
        headers: getAuthHeader()
      });
      
      const task = statusResponse.data;
      
      if (onProgress) {
        onProgress(task.progress, task.status);
      }
      
      if (task.status === 'completed') {
        return { success: true, result: task.result };
      }
      
      if (task.status === 'failed') {
        return { 
          success: false, 
          error: task.error,
          result: task.result  // Contains rejection details
        };
      }
      
      attempts++;
    } catch (pollError) {
      console.error('Error polling regenerate task:', pollError);
      attempts++;
    }
  }
  
  throw new Error('Timeout: La regeneración tardó demasiado');
};

// Chat with article - contextual AI
export const chatWithArticle = async (dossierId, message) => {
  const response = await axios.post(`${API}/dossiers/${dossierId}/chat`, 
    { dossier_id: dossierId, message },
    { 
      headers: getAuthHeader(),
      timeout: 60000
    }
  );
  return response.data;
};

export const createDossier = async (data) => {
  const response = await axios.post(`${API}/dossiers`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateDossier = async (dossierId, data) => {
  const response = await axios.put(`${API}/dossiers/${dossierId}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Weekly Editions
export const getEditions = async (status) => {
  const params = status ? `?status=${status}` : '';
  const response = await axios.get(`${API}/editions${params}`);
  return response.data;
};

export const getEdition = async (editionId) => {
  const response = await axios.get(`${API}/editions/${editionId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Alias for compatibility
export const getEditionById = getEdition;

export const getLatestEdition = async () => {
  const response = await axios.get(`${API}/editions/latest/published`);
  return response.data;
};

export const createEdition = async (data) => {
  const response = await axios.post(`${API}/editions`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Generate complete weekly edition with AI
export const generateWeeklyEdition = async () => {
  const response = await axios.post(`${API}/editions/generate-weekly`, {}, { headers: getAuthHeader() });
  return response.data;
};

// Generate images for edition articles
export const generateEditionImages = async (editionId) => {
  const response = await axios.post(`${API}/editions/${editionId}/generate-images`, {}, { headers: getAuthHeader() });
  return response.data;
};

// Regenerate edition cover
export const regenerateEditionCover = async (editionId) => {
  const response = await axios.post(`${API}/editions/${editionId}/regenerate-cover`, {}, { headers: getAuthHeader() });
  return response.data;
};

// Publish tweet for edition article
export const publishEditionArticleTweet = async (editionId, articleIndex, tweetText) => {
  const response = await axios.post(
    `${API}/editions/${editionId}/article/${articleIndex}/publish-tweet`,
    { tweet_text: tweetText },
    { headers: getAuthHeader() }
  );
  return response.data;
};

// Publish entire edition to X + Instagram (cover + all articles)
export const publishEditionToSocial = async (editionId) => {
  const response = await axios.post(`${API}/editions/${editionId}/publish-social`, {}, { headers: getAuthHeader() });
  return response.data;
};

// Regenerate image for a single edition article
export const regenerateEditionArticleImage = async (editionId, articleIndex) => {
  const response = await axios.post(`${API}/editions/${editionId}/article/${articleIndex}/regenerate-image`, {}, { headers: getAuthHeader() });
  return response.data;
};


// Add AI-generated article to edition
export const addArticleToEditionAI = async (editionId, topic) => {
  const response = await adminAxios.post(`/editions/${editionId}/add-article-ai`, { topic }, { headers: getAuthHeader() });
  return response.data;
};

// Publish dossier to Instagram manually
export const publishDossierInstagram = async (dossierId) => {
  const response = await adminAxios.post(`/dossiers/${dossierId}/publish-instagram`, {}, { headers: getAuthHeader() });
  return response.data;
};


// Publish edition article to Instagram
export const publishEditionArticleInstagram = async (editionId, articleIndex) => {
  const response = await adminAxios.post(`/editions/${editionId}/article/${articleIndex}/publish-instagram`, {}, { headers: getAuthHeader() });
  return response.data;
};


// ============================================
// LINKEDIN PUBLISHER
// ============================================

// Publish dossier to LinkedIn
export const publishDossierLinkedIn = async (dossierId) => {
  const response = await adminAxios.post(`/dossiers/${dossierId}/publish-linkedin`, {}, { headers: getAuthHeader() });
  return response.data;
};

// Preview LinkedIn content without publishing
export const previewLinkedInContent = async (dossierId) => {
  const response = await adminAxios.post(`/linkedin/preview`, { dossier_id: dossierId }, { headers: getAuthHeader() });
  return response.data;
};








export const updateEdition = async (editionId, data) => {
  const response = await axios.put(`${API}/editions/${editionId}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Evidence
export const getEvidence = async (filters = {}) => {
  const params = new URLSearchParams(filters).toString();
  const response = await axios.get(`${API}/evidence?${params}`);
  return response.data;
};

export const getEvidenceItem = async (evidenceId) => {
  const response = await axios.get(`${API}/evidence/${evidenceId}`);
  return response.data;
};

// Alias for compatibility
export const getEvidenceById = getEvidenceItem;

export const createEvidence = async (data) => {
  const response = await axios.post(`${API}/evidence`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateEvidence = async (evidenceId, data) => {
  const response = await axios.put(`${API}/evidence/${evidenceId}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const deleteEvidence = async (evidenceId) => {
  const response = await axios.delete(`${API}/evidence/${evidenceId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Influence Index
export const getInfluenceIndex = async (filters = {}) => {
  const params = new URLSearchParams(filters).toString();
  const response = await axios.get(`${API}/influence-index?${params}`);
  return response.data;
};

export const getInfluenceEntry = async (entryId) => {
  const response = await axios.get(`${API}/influence-index/${entryId}`);
  return response.data;
};

export const createInfluenceEntry = async (data) => {
  const response = await axios.post(`${API}/influence-index`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateInfluenceEntry = async (entryId, data) => {
  const response = await axios.put(`${API}/influence-index/${entryId}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Publish tweet for dossier
export const publishTweetForDossier = async (dossierId, tweetText) => {
  const response = await adminAxios.post(
    `/dossiers/${dossierId}/publish-tweet`,
    { tweet_text: tweetText },
    { headers: getAuthHeader() }
  );
  return response.data;
};

// Improve tweet with AI using custom instructions
export const improveTweetForDossier = async (dossierId, currentTweet, improvementInstructions) => {
  const response = await axios.post(
    `${API}/dossiers/${dossierId}/improve-tweet`,
    { 
      current_tweet: currentTweet,
      improvement_instructions: improvementInstructions
    },
    { headers: getAuthHeader() }
  );
  return response.data;
};

// Regenerate tweet suggestion with AI
export const regenerateTweet = async (dossierId) => {
  const response = await axios.post(
    `${API}/dossiers/${dossierId}/regenerate-tweet`,
    {},
    { headers: getAuthHeader() }
  );
  return response.data;
};

// Create article from topic using AI + Firecrawl research
export const createArticleFromTopic = async (topic, instructions = '', useFirecrawl = true) => {
  const response = await axios.post(
    `${API}/dossiers/create-from-topic`,
    { topic, instructions, use_firecrawl: useFirecrawl },
    { headers: getAuthHeader() }
  );
  return response.data;
};

// Get AI task status
export const getAITaskStatus = async (taskId) => {
  const response = await axios.get(`${API}/ai-tasks/${taskId}`, { headers: getAuthHeader() });
  return response.data;
};


export const deleteInfluenceEntry = async (entryId) => {
  const response = await axios.delete(`${API}/influence-index/${entryId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Dossier delete
export const deleteDossier = async (dossierId) => {
  const response = await axios.delete(`${API}/dossiers/${dossierId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Edition delete
export const deleteEdition = async (editionId) => {
  const response = await axios.delete(`${API}/editions/${editionId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Chat
export const sendChatMessage = async (data) => {
  const response = await axios.post(`${API}/chat`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

// AI Agents
export const runDossierBuilder = async (data) => {
  const response = await axios.post(`${API}/agents/dossier-builder`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const runRoleViews = async (data) => {
  const response = await axios.post(`${API}/agents/role-views`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const runClaimExtractor = async (data) => {
  const response = await axios.post(`${API}/agents/claim-extractor`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const runWeeklyPlanner = async (data) => {
  const response = await axios.post(`${API}/agents/weekly-planner`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const runAudioScript = async (data) => {
  const response = await axios.post(`${API}/agents/audio-script`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Audio
export const generateAudio = async (data) => {
  const response = await axios.post(`${API}/audio/generate`, data, {
    headers: getAuthHeader(),
    responseType: 'blob'
  });
  return response.data;
};

// Subscription
export const createCheckoutSession = async (planId) => {
  const response = await axios.post(`${API}/subscription/checkout`, {
    plan_id: planId,
    origin_url: window.location.origin
  }, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getCheckoutStatus = async (sessionId) => {
  const response = await axios.get(`${API}/subscription/status/${sessionId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// User Actions
export const saveDossier = async (dossierId) => {
  const response = await axios.post(`${API}/user/save-dossier/${dossierId}`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const unsaveDossier = async (dossierId) => {
  const response = await axios.delete(`${API}/user/save-dossier/${dossierId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getSavedItems = async () => {
  const response = await axios.get(`${API}/user/saved-items`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Editorial Tasks
export const getEditorialTasks = async (status) => {
  const params = status ? `?status=${status}` : '';
  const response = await axios.get(`${API}/editorial-tasks${params}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const createEditorialTask = async (data) => {
  const response = await axios.post(`${API}/editorial-tasks`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateEditorialTask = async (taskId, data) => {
  const response = await axios.put(`${API}/editorial-tasks/${taskId}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Search
export const globalSearch = async (query) => {
  const response = await axios.get(`${API}/search?q=${encodeURIComponent(query)}`);
  return response.data;
};

// Stats
export const getStats = async () => {
  const response = await axios.get(`${API}/stats`);
  return response.data;
};

// Scraping
export const runScrapingNow = async () => {
  const response = await axios.post(`${API}/scraping/run`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getScrapingStatus = async () => {
  const response = await axios.get(`${API}/scraping/status`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const toggleScraping = async (enable) => {
  const response = await axios.post(`${API}/scraping/toggle`, { enable }, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getScrapingHistory = async (limit = 20) => {
  const response = await axios.get(`${API}/scraping/history?limit=${limit}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Staff Management
export const getStaffMembers = async () => {
  const response = await axios.get(`${API}/staff`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const createStaffMember = async (data) => {
  const response = await axios.post(`${API}/staff`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateStaffMember = async (userId, data) => {
  const response = await axios.put(`${API}/staff/${userId}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const deleteStaffMember = async (userId) => {
  const response = await axios.delete(`${API}/staff/${userId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getAllUsers = async (userType = null) => {
  const params = userType ? `?user_type=${userType}` : '';
  const response = await axios.get(`${API}/users${params}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Content Generation
export const generateDossier = async (data) => {
  const response = await axios.post(`${API}/generate/dossier`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const generateEdition = async (data) => {
  const response = await axios.post(`${API}/generate/edition`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const generateInfluenceEntry = async (data) => {
  const response = await axios.post(`${API}/generate/influence`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const publishContent = async (contentType, contentId) => {
  const response = await axios.post(`${API}/content/${contentType}/${contentId}/publish`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const getDraftContent = async () => {
  const response = await axios.get(`${API}/content/drafts`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Manual Scraping Flow
export const manualScrapeSearch = async (data) => {
  const response = await axios.post(`${API}/scraping/manual/search`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const manualAdaptContent = async (data) => {
  const response = await axios.post(`${API}/scraping/manual/adapt`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const manualPublishArticles = async (data) => {
  const response = await axios.post(`${API}/scraping/manual/publish`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

// ==================== X MONITOR & STORIES ====================

// Stories
export const getStories = async (status = 'ACTIVE') => {
  return retryRequest(async () => {
    const response = await adminAxios.get(`/stories?status=${status}`, {
      headers: getAuthHeader()
    });
    return response.data;
  });
};

export const getStoryDetail = async (storyId) => {
  const response = await axios.get(`${API}/stories/${storyId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const createStory = async (data) => {
  const response = await axios.post(`${API}/stories`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const updateStory = async (storyId, data) => {
  const response = await axios.patch(`${API}/stories/${storyId}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const addStoryUpdate = async (storyId, data) => {
  const response = await axios.post(`${API}/stories/${storyId}/updates`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Retry story generation for failed stories
export const retryStoryGeneration = async (storyId) => {
  const response = await axios.post(`${API}/stories/${storyId}/retry-generation`, {}, {
    headers: getAuthHeader(),
    timeout: 15000  // 15 seconds - backend returns immediately, generation happens in background
  });
  return response.data;
};

// Delete a story
export const deleteStory = async (storyId) => {
  const response = await axios.delete(`${API}/stories/${storyId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Pending Approvals
export const getPendingApprovals = async (status = 'PENDING', limit = 50, page = 1) => {
  return retryRequest(async () => {
    const response = await adminAxios.get(`/pending-approvals?status=${status}&limit=${limit}&page=${page}`, {
      headers: getAuthHeader()
    });
    return response.data;
  });
};

// Get high relevance notifications (score > 50)
export const getHighRelevanceNotifications = async () => {
  return retryRequest(async () => {
    const response = await adminAxios.get(`/pending-approvals/high-relevance`, {
      headers: getAuthHeader()
    });
    return response.data;
  });
};

// Bulk Process Approvals - Approve or Reject multiple at once
export const bulkProcessApprovals = async (approvalIds, action) => {
  const response = await axios.post(`${API}/pending-approvals/bulk-action`, 
    { approval_ids: approvalIds, action: action },
    {
      headers: getAuthHeader(),
      timeout: 10000  // 10 seconds - backend returns immediately, generation happens in background
    }
  );
  return response.data;
};

// Process Approval - Approve or Reject (article generation happens in background)
export const processApproval = async (approvalId, action) => {
  const response = await axios.post(`${API}/pending-approvals/${approvalId}/action`, action, {
    headers: getAuthHeader(),
    timeout: 5000  // 5 seconds - backend returns immediately, generation happens in background
  });
  return response.data;
};

// X Monitor
export const runXMonitor = async () => {
  const response = await axios.post(`${API}/x-monitor/run`, {}, {
    headers: getAuthHeader(),
    timeout: 120000  // 2 minute timeout for monitoring
  });
  return response.data;
};

export const getXMonitorStatus = async () => {
  return retryRequest(async () => {
    const response = await adminAxios.get(`/x-monitor/status`, {
      headers: getAuthHeader()
    });
    return response.data;
  });
};

export const getIngestedTweets = async (status = 'all') => {
  const response = await axios.get(`${API}/tweets-ingested?status=${status}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// X Monitor Accounts Management
export const updateMonitoredAccounts = async (accounts) => {
  const response = await axios.put(`${API}/x-monitor/accounts`, { accounts }, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const addMonitoredAccount = async (account, autoPublish = false) => {
  const response = await axios.post(`${API}/x-monitor/accounts/add`, { account, auto_publish: autoPublish }, {
    headers: getAuthHeader()
  });
  return response.data;
};

export const removeMonitoredAccount = async (account) => {
  const response = await axios.delete(`${API}/x-monitor/accounts/${account}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Toggle auto-publish for an account
export const toggleAutoPublish = async (account, autoPublish) => {
  const response = await axios.put(`${API}/x-monitor/accounts/${account}/auto-publish`, 
    { auto_publish: autoPublish }, 
    { headers: getAuthHeader() }
  );
  return response.data;
};

// Get auto-publish activity log
export const getAutoPublishLog = async (limit = 50) => {
  const response = await axios.get(`${API}/x-monitor/auto-publish/log?limit=${limit}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Get auto-publish stats
export const getAutoPublishStats = async () => {
  const response = await axios.get(`${API}/x-monitor/auto-publish/stats`, {
    headers: getAuthHeader()
  });
  return response.data;
};


// ======================= SCHEDULER CONTROL =======================

// Get scheduler status
export const getSchedulerStatus = async () => {
  const response = await axios.get(`${API}/x-monitor/scheduler/status`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Pause scheduler
export const pauseScheduler = async () => {
  const response = await axios.post(`${API}/x-monitor/scheduler/pause`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Resume scheduler  
export const resumeScheduler = async () => {
  const response = await axios.post(`${API}/x-monitor/scheduler/resume`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Set scheduler interval
export const setSchedulerInterval = async (intervalMinutes) => {
  const response = await axios.put(`${API}/x-monitor/scheduler/interval`, 
    { interval_minutes: intervalMinutes },
    { headers: getAuthHeader() }
  );
  return response.data;
};


// ======================= DATA MIGRATION =======================

// Export all data to JSON
export const exportData = async () => {
  const response = await axios.get(`${API}/admin/export-data`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Import data from JSON
export const importData = async (data, mode = 'merge') => {
  const response = await axios.post(`${API}/admin/import-data?mode=${mode}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Get download URL for export
export const getExportDownloadUrl = () => {
  return `${API}/admin/export-data/download`;
};

// ======================= SITE CONFIGURATION =======================

// Get site config (public - no auth needed for categories, navigation, footer, seo)
export const getSiteConfig = async (configType) => {
  const response = await axios.get(`${API}/config/${configType}`);
  return response.data;
};

// Update site config (admin only)
export const updateSiteConfig = async (configType, data) => {
  const response = await axios.put(`${API}/config/${configType}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Reset site config to defaults (admin only)
export const resetSiteConfig = async (configType) => {
  const response = await axios.post(`${API}/config/${configType}/reset`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Add category
export const addCategory = async (category) => {
  const response = await axios.post(`${API}/config/categories/add`, category, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Delete category
export const deleteCategory = async (categoryId) => {
  const response = await axios.delete(`${API}/config/categories/${categoryId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Publish edition
export const publishEdition = async (editionId) => {
  const response = await axios.post(`${API}/editions/${editionId}/publish`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Unpublish edition
export const unpublishEdition = async (editionId) => {
  const response = await axios.post(`${API}/editions/${editionId}/unpublish`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Suggest edition content with AI
export const suggestEditionContent = async (data) => {
  const response = await axios.post(`${API}/editions/suggest-content`, data, {
    headers: getAuthHeader(),
    timeout: 60000
  });
  return response.data;
};

// Suggest footer links with AI
export const suggestFooterLinks = async (data) => {
  const response = await axios.post(`${API}/config/footer/suggest-links`, data, {
    headers: getAuthHeader(),
    timeout: 60000
  });
  return response.data;
};

// ======================= STATIC PAGES =======================

// Get all static pages
export const getStaticPages = async (publishedOnly = false) => {
  const params = publishedOnly ? '?published_only=true' : '';
  const response = await axios.get(`${API}/pages${params}`);
  return response.data;
};

// Get single static page by slug
export const getStaticPage = async (slug) => {
  const response = await axios.get(`${API}/pages/${slug}`);
  return response.data;
};

// Create static page
export const createStaticPage = async (data) => {
  const response = await axios.post(`${API}/pages`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Update static page
export const updateStaticPage = async (slug, data) => {
  const response = await axios.put(`${API}/pages/${slug}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Delete static page
export const deleteStaticPage = async (slug) => {
  const response = await axios.delete(`${API}/pages/${slug}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Publish static page
export const publishStaticPage = async (slug) => {
  const response = await axios.post(`${API}/pages/${slug}/publish`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Unpublish static page
export const unpublishStaticPage = async (slug) => {
  const response = await axios.post(`${API}/pages/${slug}/unpublish`, {}, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Generate page content with AI
export const generatePageContent = async (data) => {
  const response = await axios.post(`${API}/pages/generate-content`, data, {
    headers: getAuthHeader(),
    timeout: 120000
  });
  return response.data;
};

// ======================= EDITOR ARTICLE CREATION =======================

// Improve article text with AI
export const improveArticleText = async (data) => {
  const response = await axios.post(`${API}/editor/improve-text`, data, {
    headers: getAuthHeader(),
    timeout: 60000
  });
  return response.data;
};

// Generate article cover image with AI
export const generateArticleImage = async (data) => {
  const response = await axios.post(`${API}/editor/generate-image`, data, {
    headers: getAuthHeader(),
    timeout: 120000
  });
  return response.data;
};

// Create article (editor flow)
export const createArticle = async (data) => {
  const response = await axios.post(`${API}/editor/articles`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

// ======================= DESIGNER IMAGE GALLERY =======================

// Get all generated images
export const getGeneratedImages = async () => {
  const response = await axios.get(`${API}/designer/gallery`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Generate standalone image (not tied to an article)
export const generateStandaloneImage = async (data) => {
  const response = await axios.post(`${API}/designer/generate-image`, data, {
    headers: getAuthHeader(),
    timeout: 120000
  });
  return response.data;
};

// Delete generated image
export const deleteGeneratedImage = async (imageId) => {
  const response = await axios.delete(`${API}/designer/images/${imageId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// ======================= BLOG POSTS =======================

// Get all blog posts
export const getBlogPosts = async (filters = {}) => {
  const params = new URLSearchParams(filters).toString();
  const response = await axios.get(`${API}/blog?${params}`);
  return response.data;
};

// Get my blog posts (for editors)
export const getMyBlogPosts = async () => {
  const response = await axios.get(`${API}/blog/my-posts`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Get single blog post
export const getBlogPost = async (blogId) => {
  const response = await axios.get(`${API}/blog/${blogId}`);
  return response.data;
};

// Create blog post
export const createBlogPost = async (data) => {
  const response = await axios.post(`${API}/blog`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Update blog post
export const updateBlogPost = async (blogId, data) => {
  const response = await axios.put(`${API}/blog/${blogId}`, data, {
    headers: getAuthHeader()
  });
  return response.data;
};

// Delete blog post
export const deleteBlogPost = async (blogId) => {
  const response = await axios.delete(`${API}/blog/${blogId}`, {
    headers: getAuthHeader()
  });
  return response.data;
};

// ======================= EDITOR: MY ARTICLES =======================

// Get articles created by current editor
export const getMyArticles = async () => {
  const response = await axios.get(`${API}/editor/my-articles`, {
    headers: getAuthHeader()
  });
  return response.data;
};


// Reels
export const generateReel = async (data) => {
  const response = await adminAxios.post('/reels/generate', data, { headers: getAuthHeader() });
  return response.data;
};

export const listReels = async () => {
  const response = await axios.get(`${API}/reels`, { headers: getAuthHeader() });
  return response.data;
};

export const deleteReel = async (reelId) => {
  const response = await axios.delete(`${API}/reels/${reelId}`, { headers: getAuthHeader() });
  return response.data;
};

export const getReelsAutopostConfig = async () => {
  const response = await axios.get(`${API}/reels/autopost-config`, { headers: getAuthHeader() });
  return response.data;
};

export const saveReelsAutopostConfig = async (config) => {
  const response = await axios.put(`${API}/reels/autopost-config`, config, { headers: getAuthHeader() });
  return response.data;
};

export const getProductionTopNews = async () => {
  const response = await axios.get(`${API}/production/top-news`, { headers: getAuthHeader() });
  return response.data;
};

export const publishReelInstagram = async (reelId) => {
  const response = await axios.post(`${API}/reels/${reelId}/publish-instagram`, {}, { headers: getAuthHeader() });
  return response.data;
};
